import { z } from 'zod';
// Enums
export var TaskStatus;
(function (TaskStatus) {
    TaskStatus["PENDING"] = "PENDING";
    TaskStatus["IN_PROGRESS"] = "IN_PROGRESS";
    TaskStatus["COMPLETED"] = "COMPLETED";
    TaskStatus["CANCELLED"] = "CANCELLED";
    TaskStatus["OVERDUE"] = "OVERDUE";
    TaskStatus["WAITING_APPROVAL"] = "WAITING_APPROVAL";
    TaskStatus["APPROVED"] = "APPROVED";
    TaskStatus["REJECTED"] = "REJECTED";
})(TaskStatus || (TaskStatus = {}));
export var TaskPriority;
(function (TaskPriority) {
    TaskPriority["LOW"] = "LOW";
    TaskPriority["MEDIUM"] = "MEDIUM";
    TaskPriority["HIGH"] = "HIGH";
    TaskPriority["URGENT"] = "URGENT";
})(TaskPriority || (TaskPriority = {}));
export var TaskType;
(function (TaskType) {
    TaskType["GENERAL"] = "GENERAL";
    TaskType["LEAD_FOLLOWUP"] = "LEAD_FOLLOWUP";
    TaskType["ADMISSION_PROCESS"] = "ADMISSION_PROCESS";
    TaskType["STUDENT_ISSUE"] = "STUDENT_ISSUE";
    TaskType["MAINTENANCE"] = "MAINTENANCE";
    TaskType["ADMINISTRATIVE"] = "ADMINISTRATIVE";
    TaskType["ACADEMIC"] = "ACADEMIC";
    TaskType["HR_TASK"] = "HR_TASK";
    TaskType["PAYROLL_TASK"] = "PAYROLL_TASK";
})(TaskType || (TaskType = {}));
// Zod Schemas
export const TaskSchema = z.object({
    id: z.string().uuid(),
    title: z.string().min(1).max(200),
    description: z.string().optional(),
    type: z.nativeEnum(TaskType).default(TaskType.GENERAL),
    status: z.nativeEnum(TaskStatus).default(TaskStatus.PENDING),
    priority: z.nativeEnum(TaskPriority).default(TaskPriority.MEDIUM),
    // Assignment
    assignedTo: z.string().uuid(),
    assignedBy: z.string().uuid(),
    tenantId: z.string().uuid(),
    // Related entities
    leadId: z.string().uuid().optional(),
    studentId: z.string().uuid().optional(),
    schoolId: z.string().uuid().optional(),
    // Dates
    dueDate: z.date(),
    startDate: z.date().optional(),
    completedAt: z.date().optional(),
    // Approval workflow
    requiresApproval: z.boolean().default(false),
    approvedBy: z.string().uuid().optional(),
    approvedAt: z.date().optional(),
    approvalComments: z.string().optional(),
    rejectionReason: z.string().optional(),
    // Progress tracking
    progressPercentage: z.number().min(0).max(100).default(0),
    timeSpent: z.number().default(0), // in minutes
    estimatedTime: z.number().optional(), // in minutes
    // Attachments and notes
    attachments: z.array(z.object({
        id: z.string().uuid(),
        filename: z.string(),
        url: z.string(),
        uploadedBy: z.string().uuid(),
        uploadedAt: z.date(),
    })).default([]),
    notes: z.array(z.object({
        id: z.string().uuid(),
        content: z.string(),
        createdBy: z.string().uuid(),
        createdAt: z.date(),
    })).default([]),
    // Metadata
    tags: z.array(z.string()).default([]),
    customFields: z.record(z.string(), z.any()).default({}),
    createdAt: z.date(),
    updatedAt: z.date(),
});
export const CreateTaskSchema = TaskSchema.omit({
    id: true,
    status: true,
    progressPercentage: true,
    timeSpent: true,
    completedAt: true,
    approvedBy: true,
    approvedAt: true,
    approvalComments: true,
    rejectionReason: true,
    attachments: true,
    notes: true,
    createdAt: true,
    updatedAt: true,
});
export const UpdateTaskSchema = z.object({
    title: z.string().min(1).max(200).optional(),
    description: z.string().optional(),
    type: z.nativeEnum(TaskType).optional(),
    status: z.nativeEnum(TaskStatus).optional(),
    priority: z.nativeEnum(TaskPriority).optional(),
    assignedTo: z.string().uuid().optional(),
    dueDate: z.date().optional(),
    startDate: z.date().optional(),
    progressPercentage: z.number().min(0).max(100).optional(),
    timeSpent: z.number().optional(),
    estimatedTime: z.number().optional(),
    tags: z.array(z.string()).optional(),
    customFields: z.record(z.string(), z.any()).optional(),
});
export const TaskApprovalSchema = z.object({
    action: z.enum(['APPROVE', 'REJECT']),
    comments: z.string().optional(),
    rejectionReason: z.string().optional(),
});
export const TaskNoteSchema = z.object({
    content: z.string().min(1),
});
export const TaskAttachmentSchema = z.object({
    filename: z.string(),
    url: z.string(),
});
// Task Filter Schema
export const TaskFilterSchema = z.object({
    status: z.array(z.nativeEnum(TaskStatus)).optional(),
    priority: z.array(z.nativeEnum(TaskPriority)).optional(),
    type: z.array(z.nativeEnum(TaskType)).optional(),
    assignedTo: z.array(z.string().uuid()).optional(),
    assignedBy: z.array(z.string().uuid()).optional(),
    leadId: z.string().uuid().optional(),
    studentId: z.string().uuid().optional(),
    dueDateFrom: z.date().optional(),
    dueDateTo: z.date().optional(),
    tags: z.array(z.string()).optional(),
    search: z.string().optional(),
    requiresApproval: z.boolean().optional(),
});
// Task Factory
export class TaskFactory {
    static createTask(data) {
        const task = {
            id: crypto.randomUUID(),
            status: TaskStatus.PENDING,
            progressPercentage: 0,
            timeSpent: 0,
            attachments: [],
            notes: [],
            createdAt: new Date(),
            updatedAt: new Date(),
            ...data,
        };
        return TaskSchema.parse(task);
    }
    static createLeadFollowupTask(leadId, assignedTo, assignedBy, tenantId, dueDate, description) {
        return this.createTask({
            title: 'Lead Follow-up Required',
            description: description || 'Follow up with lead to progress the admission process',
            type: TaskType.LEAD_FOLLOWUP,
            priority: TaskPriority.HIGH,
            assignedTo,
            assignedBy,
            tenantId,
            leadId,
            dueDate,
            requiresApproval: false,
            tags: [],
            customFields: {},
        });
    }
    static createAdmissionTask(leadId, assignedTo, assignedBy, tenantId, dueDate, requiresApproval = true) {
        return this.createTask({
            title: 'Process Admission Application',
            description: 'Complete the admission process for the prospective student',
            type: TaskType.ADMISSION_PROCESS,
            priority: TaskPriority.HIGH,
            assignedTo,
            assignedBy,
            tenantId,
            leadId,
            dueDate,
            requiresApproval,
            tags: [],
            customFields: {},
        });
    }
}
// Task Permissions
export class TaskPermissions {
    static canCreateTask(userRole) {
        return ![
            'STUDENT',
            'PARENT',
        ].includes(userRole);
    }
    static canAssignTask(userRole, assigneeRole) {
        const roleHierarchy = {
            'SUPER_ADMIN': 100,
            'ORG_ADMIN': 90,
            'PRINCIPAL': 80,
            'HR_MANAGER': 70,
            'SALES_MANAGER': 65,
            'TRANSPORT_MANAGER': 60,
            'ACCOUNTANT': 55,
            'TEACHER': 50,
            'SALES_REP': 40,
            'DRIVER': 30,
            'PARENT': 20,
            'STUDENT': 10,
        };
        const userLevel = roleHierarchy[userRole] || 0;
        const assigneeLevel = roleHierarchy[assigneeRole] || 0;
        return userLevel >= assigneeLevel;
    }
    static canViewTask(userRole, userId, task) {
        // Super admin and org admin can view all tasks
        if (['SUPER_ADMIN', 'ORG_ADMIN'].includes(userRole)) {
            return true;
        }
        // Users can view tasks assigned to them or created by them
        if (task.assignedTo === userId || task.assignedBy === userId) {
            return true;
        }
        // Principals can view all tasks in their organization
        if (userRole === 'PRINCIPAL') {
            return true;
        }
        // Sales managers can view all sales-related tasks
        if (userRole === 'SALES_MANAGER' &&
            [TaskType.LEAD_FOLLOWUP, TaskType.ADMISSION_PROCESS].includes(task.type)) {
            return true;
        }
        // HR managers can view HR-related tasks
        if (userRole === 'HR_MANAGER' &&
            [TaskType.HR_TASK, TaskType.PAYROLL_TASK].includes(task.type)) {
            return true;
        }
        return false;
    }
    static canUpdateTask(userRole, userId, task) {
        // Super admin and org admin can update all tasks
        if (['SUPER_ADMIN', 'ORG_ADMIN'].includes(userRole)) {
            return true;
        }
        // Task creator can update the task
        if (task.assignedBy === userId) {
            return true;
        }
        // Assigned user can update progress and status
        if (task.assignedTo === userId) {
            return true;
        }
        // Principals can update all tasks in their organization
        if (userRole === 'PRINCIPAL') {
            return true;
        }
        return false;
    }
    static canApproveTask(userRole, userId, task) {
        if (!task.requiresApproval) {
            return false;
        }
        // Super admin and org admin can approve all tasks
        if (['SUPER_ADMIN', 'ORG_ADMIN'].includes(userRole)) {
            return true;
        }
        // Task creator can approve their assigned tasks
        if (task.assignedBy === userId) {
            return true;
        }
        // Principals can approve all tasks in their organization
        if (userRole === 'PRINCIPAL') {
            return true;
        }
        // Department heads can approve tasks in their domain
        if (userRole === 'SALES_MANAGER' &&
            [TaskType.LEAD_FOLLOWUP, TaskType.ADMISSION_PROCESS].includes(task.type)) {
            return true;
        }
        if (userRole === 'HR_MANAGER' &&
            [TaskType.HR_TASK, TaskType.PAYROLL_TASK].includes(task.type)) {
            return true;
        }
        return false;
    }
    static canDeleteTask(userRole, userId, task) {
        // Only super admin, org admin, and task creator can delete tasks
        if (['SUPER_ADMIN', 'ORG_ADMIN'].includes(userRole)) {
            return true;
        }
        if (task.assignedBy === userId) {
            return true;
        }
        return false;
    }
}
// Task Status Transitions
export class TaskStatusTransitions {
    static allowedTransitions = {
        [TaskStatus.PENDING]: [TaskStatus.IN_PROGRESS, TaskStatus.CANCELLED],
        [TaskStatus.IN_PROGRESS]: [TaskStatus.COMPLETED, TaskStatus.WAITING_APPROVAL, TaskStatus.CANCELLED],
        [TaskStatus.COMPLETED]: [TaskStatus.IN_PROGRESS], // Allow reopening
        [TaskStatus.WAITING_APPROVAL]: [TaskStatus.APPROVED, TaskStatus.REJECTED, TaskStatus.IN_PROGRESS],
        [TaskStatus.APPROVED]: [],
        [TaskStatus.REJECTED]: [TaskStatus.IN_PROGRESS, TaskStatus.CANCELLED],
        [TaskStatus.CANCELLED]: [TaskStatus.PENDING], // Allow reactivation
        [TaskStatus.OVERDUE]: [TaskStatus.IN_PROGRESS, TaskStatus.COMPLETED, TaskStatus.CANCELLED],
    };
    static canTransition(from, to) {
        return this.allowedTransitions[from]?.includes(to) || false;
    }
    static getNextPossibleStatuses(currentStatus) {
        return this.allowedTransitions[currentStatus] || [];
    }
    static shouldRequireApproval(task, newStatus) {
        return task.requiresApproval && newStatus === TaskStatus.COMPLETED;
    }
}
//# sourceMappingURL=Task.js.map